import React, { useEffect, useState } from "react";
import { X, Calendar, User, BookOpen, Clock, FileText, ChevronDown, ChevronUp, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminObservationsByTeacher } from "../../redux/adminObservationSlice";
import jsPDF from "jspdf";
import toast from "react-hot-toast";

const ViewAdminObservationsModal = ({ isOpen, onClose, teacherEmail, teacherDisplayName, campusClasses, onAddNew }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { observations, loading } = useSelector((state) => state.adminObservation);
  const [expandedObservationId, setExpandedObservationId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (isOpen && teacherEmail) {
      dispatch(fetchAdminObservationsByTeacher(teacherEmail));
    }
  }, [isOpen, teacherEmail, dispatch]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      if (timeString.includes(":")) {
        const parts = timeString.split(":");
        return `${parts[0]}:${parts[1]}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const d = new Date(dateTimeString);
      if (isNaN(d.getTime())) return dateTimeString;
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateTimeString;
    }
  };

  const toggleSection = (observationId, sectionName) => {
    const key = `${observationId}-${sectionName}`;
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isSectionExpanded = (observationId, sectionName) => {
    const key = `${observationId}-${sectionName}`;
    return expandedSections[key] || false;
  };

  const getClassName = (classId) => {
    if (!classId || !campusClasses) return classId || "N/A";
    const found = campusClasses
      .flatMap((c) => c.classes || [])
      .find((cls) => cls.id === classId);
    return found?.className || classId;
  };

  const getSubjectName = (subjectId) => {
    if (!subjectId || !campusClasses) return subjectId || "N/A";
    const found = campusClasses
      .flatMap((c) => c.classes || [])
      .flatMap((cls) => cls.sections || [])
      .flatMap((s) => s.subjects || [])
      .find((sub) => sub.id === subjectId);
    return found?.subjectName || subjectId;
  };

  // Add one observation's content to PDF doc; returns new y position
  const addObservationToPdf = (doc, observation, observationNumber, yStart, teacherName) => {
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;
    let y = yStart;
    const lineHeight = 6;
    const sectionGap = 8;

    const addText = (text, bold = false) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.setFont("helvetica", bold ? "bold" : "normal");
      const lines = doc.splitTextToSize(String(text || "N/A"), maxWidth);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + 2;
    };

    const addSection = (title, content) => {
      if (y > 260) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(title, margin, y);
      y += lineHeight + 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(String(content || "N/A"), maxWidth);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + sectionGap;
    };

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`Observation ${observationNumber}`, margin, y);
    y += lineHeight + 4;

    const bi = observation.basicInformation || {};
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    addText(`Date: ${formatDate(bi.observationDate)}`);
    addText(`Observer: ${bi.observerName || "N/A"} | Type: ${bi.observationType || "N/A"}`);
    addText(`Start: ${formatDateTime(bi.startDurationObservation)} | End: ${formatDateTime(bi.endDurationObservation)}`);
    addText(`Class Grade: ${getClassName(bi.classGrade)} | Subject: ${getSubjectName(bi.subject)}`);
    addText(`Teacher: ${teacherName || bi.teacherName || "N/A"} | Observer Designation: ${bi.observerDesignation || "N/A"}`);
    addText(`Lesson Topic: ${bi.lessonTopic || "N/A"}`);
    y += sectionGap;

    if (observation.classroomEnvironment) {
      const ce = observation.classroomEnvironment;
      doc.setFont("helvetica", "bold");
      doc.text("Classroom Environment", margin, y);
      y += lineHeight + 2;
      doc.setFont("helvetica", "normal");
      addSection("Classroom Organization", ce.classroomOrganization);
      addSection("Student Engagement", ce.studentEngagement);
      addSection("Use of Learning Materials", ce.useOfLearningMaterials + (ce.useOfLearningMaterialsDetails ? " - " + ce.useOfLearningMaterialsDetails : ""));
      addSection("Inclusivity", ce.inclusivity + (ce.inclusivityDetails ? " - " + ce.inclusivityDetails : ""));
      addSection("Teacher Student Interaction", ce.teacherStudentInteraction);
      y += sectionGap;
    }

    if (observation.instructionalDelivery) {
      const id = observation.instructionalDelivery;
      doc.setFont("helvetica", "bold");
      doc.text("Instructional Delivery", margin, y);
      y += lineHeight + 2;
      doc.setFont("helvetica", "normal");
      addSection("Lesson Planning", id.lessonPlanning + (id.lessonPlanningDetails ? " - " + id.lessonPlanningDetails : ""));
      addSection("Teaching Strategies", id.teachingStrategies);
      addSection("Questioning Techniques", id.questioningTechniques);
      addSection("Pace & Time Management", id.paceTimeManagement);
      addSection("Content Knowledge", id.contentKnowledge);
      addSection("Technology Integration", id.technologyIntegration + (id.technologyIntegrationDetails ? " - " + id.technologyIntegrationDetails : ""));
      y += sectionGap;
    }

    if (observation.studentLearningAssessment) {
      const sla = observation.studentLearningAssessment;
      doc.setFont("helvetica", "bold");
      doc.text("Student Learning & Assessment", margin, y);
      y += lineHeight + 2;
      doc.setFont("helvetica", "normal");
      addSection("Assessment Methods", sla.assessmentMethods + (sla.assessmentMethodsDetails ? " - " + sla.assessmentMethodsDetails : ""));
      addSection("Student Participation", sla.studentParticipation + (sla.studentParticipationDetails ? " - " + sla.studentParticipationDetails : ""));
      addSection("Feedback Provided", sla.feedbackProvided + (sla.feedbackProvidedDetails ? " - " + sla.feedbackProvidedDetails : ""));
      addSection("Evidence of Learning", sla.evidenceOfLearning + (sla.evidenceOfLearningDetails ? " - " + sla.evidenceOfLearningDetails : ""));
      y += sectionGap;
    }

    if (observation.professionalismReflection) {
      const pr = observation.professionalismReflection;
      doc.setFont("helvetica", "bold");
      doc.text("Professionalism & Reflection", margin, y);
      y += lineHeight + 2;
      doc.setFont("helvetica", "normal");
      addSection("Preparedness & Punctuality", pr.preparedness + (pr.preparednessDetails ? " - " + pr.preparednessDetails : ""));
      addSection("Professional Demeanor", pr.professionalDemeanor + (pr.professionalDemeanorDetails ? " - " + pr.professionalDemeanorDetails : ""));
      addSection("Collaboration", pr.collaboration + (pr.collaborationDetails ? " - " + pr.collaborationDetails : ""));
      addSection("Responsiveness to Feedback", pr.responsivenessToFeedback + (pr.responsivenessToFeedbackDetails ? " - " + pr.responsivenessToFeedbackDetails : ""));
      addSection("Reflective Practice", pr.reflectivePractice + (pr.reflectivePracticeDetails ? " - " + pr.reflectivePracticeDetails : ""));
      y += sectionGap;
    }

    if (observation.principalsRemarks) {
      const pm = observation.principalsRemarks;
      doc.setFont("helvetica", "bold");
      doc.text("Principal's Remarks", margin, y);
      y += lineHeight + 2;
      doc.setFont("helvetica", "normal");
      addSection("Strengths Observed", pm.strengthsObserved);
      addSection("Areas for Improvement", pm.areasForImprovement);
      addSection("Action Plan", pm.actionPlan);
      if (pm.followUpDate) addSection("Follow Up Date", formatDate(pm.followUpDate));
      addSection("Overall Rating", (pm.overallRating || "") + (pm.overallRatingDetails ? " - " + pm.overallRatingDetails : ""));
    }

    return y;
  };

  const handleDownloadObservationPdf = (observation, observationNumber) => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const teacherName = teacherDisplayName || observation.basicInformation?.teacherName || teacherEmail || "N/A";
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Admin Observation", 20, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Teacher: ${teacherName}`, 20, 22);
      addObservationToPdf(doc, observation, observationNumber, 30, teacherName);
      const safeName = (teacherEmail || "observation").replace(/[^a-zA-Z0-9]/g, "-");
      doc.save(`Admin-Observation-${observationNumber}-${safeName}.pdf`);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to download PDF");
    }
  };

  const handleDownloadAllObservationsPdf = () => {
    if (!observations?.length) {
      toast.error("No observations to download");
      return;
    }
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const teacherName = teacherDisplayName || teacherEmail || "N/A";
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Admin Observations", 20, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Teacher: ${teacherName} | Total: ${observations.length} observation(s)`, 20, 22);
      let y = 32;
      observations.forEach((obs, index) => {
        const num = observations.length - index;
        y = addObservationToPdf(doc, obs, num, y, teacherName);
        if (index < observations.length - 1) {
          y += 10;
          if (y > 250) {
            doc.addPage();
            y = 20;
          }
        }
      });
      const safeName = (teacherEmail || "observations").replace(/[^a-zA-Z0-9]/g, "-");
      doc.save(`Admin-Observations-All-${safeName}.pdf`);
      toast.success("All observations downloaded as PDF");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to download PDF");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95%] max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div>
            <h2 className="text-2xl font-bold">
              {t("Admin Observations") || "Admin Observations"}
            </h2>
            <p className="text-sm text-blue-100 mt-1">
              {teacherEmail && `${t("Teacher")}: ${teacherEmail}`}
            </p>
          </div>
          <div className="flex gap-3 items-center">
            {observations?.length > 0 && (
              <button
                onClick={handleDownloadAllObservationsPdf}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium shadow-md"
                title={t("Download all as PDF") || "Download all observations as PDF"}
              >
                <Download size={18} />
                {t("Download all as PDF") || "Download all as PDF"}
              </button>
            )}
            {onAddNew && (
              <button
                onClick={onAddNew}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium shadow-md"
              >
                <span className="text-xl">+</span> {t("Add New") || "Add New"}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-blue-800 rounded-lg"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-600">{t("Loading Observations") || "Loading observations..."}</p>
            </div>
          ) : observations && observations.length > 0 ? (
            <div className="space-y-6">
              {observations.map((observation, index) => (
                <div
                  key={observation.id || index}
                  className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden"
                >
                  {/* Observation Header */}
                  <div
                    className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 cursor-pointer"
                    onClick={() => setExpandedObservationId(expandedObservationId === observation.id ? null : observation.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            #{observations.length - index}
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">
                            {t("Observation")} {observations.length - index}
                          </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Calendar size={16} className="text-blue-500" />
                            <span className="font-medium">{t("Date")}:</span>
                            <span className="text-gray-800">{formatDate(observation.basicInformation?.observationDate)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <User size={16} className="text-blue-500" />
                            <span className="font-medium">{t("Observer")}:</span>
                            <span className="text-gray-800">{observation.basicInformation?.observerName || "N/A"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <BookOpen size={16} className="text-blue-500" />
                            <span className="font-medium">{t("Type")}:</span>
                            <span className="text-gray-800">{observation.basicInformation?.observationType || "N/A"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <Clock size={16} className="text-blue-500" />
                            <span className="font-medium">{t("Start Duration Observation") || "Start"}:</span>
                            <span className="text-gray-800">{formatDateTime(observation.basicInformation?.startDurationObservation)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <Clock size={16} className="text-blue-500" />
                            <span className="font-medium">{t("End Duration Observation") || "End"}:</span>
                            <span className="text-gray-800">{formatDateTime(observation.basicInformation?.endDurationObservation)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4 flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadObservationPdf(observation, observations.length - index);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shadow"
                          title={t("Download as PDF") || "Download this observation as PDF"}
                        >
                          <Download size={14} />
                          {t("Download as PDF") || "Download PDF"}
                        </button>
                        {expandedObservationId === observation.id ? (
                          <ChevronUp size={24} className="text-blue-500" />
                        ) : (
                          <ChevronDown size={24} className="text-blue-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedObservationId === observation.id && (
                    <div className="p-6 space-y-6">
                      {/* Basic Information */}
                      <div className="border-l-4 border-blue-500 pl-4">
                        <h4 className="text-lg font-bold text-blue-600 mb-3 flex items-center gap-2">
                          <FileText size={20} />
                          {t("Basic Information") || "Basic Information"}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="font-semibold text-gray-700">{t("Class Grade")}:</span>
                            <span className="ml-2 text-gray-800">{getClassName(observation.basicInformation?.classGrade)}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="font-semibold text-gray-700">{t("Subject")}:</span>
                            <span className="ml-2 text-gray-800">{getSubjectName(observation.basicInformation?.subject)}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="font-semibold text-gray-700">{t("Teacher Name")}:</span>
                            <span className="ml-2 text-gray-800">{teacherDisplayName || observation.basicInformation?.teacherName || "N/A"}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="font-semibold text-gray-700">{t("Observer Designation")}:</span>
                            <span className="ml-2 text-gray-800">{observation.basicInformation?.observerDesignation || "N/A"}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded md:col-span-2">
                            <span className="font-semibold text-gray-700">{t("Lesson Topic")}:</span>
                            <span className="ml-2 text-gray-800">{observation.basicInformation?.lessonTopic || "N/A"}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="font-semibold text-gray-700">{t("Start Duration Observation") || "Start Duration Observation"}:</span>
                            <span className="ml-2 text-gray-800">{formatDateTime(observation.basicInformation?.startDurationObservation)}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="font-semibold text-gray-700">{t("End Duration Observation") || "End Duration Observation"}:</span>
                            <span className="ml-2 text-gray-800">{formatDateTime(observation.basicInformation?.endDurationObservation)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Classroom Environment */}
                      {observation.classroomEnvironment && (
                        <div className="border-l-4 border-green-500 pl-4">
                          <button
                            onClick={() => toggleSection(observation.id, "classroom")}
                            className="w-full flex items-center justify-between text-left mb-3"
                          >
                            <h4 className="text-lg font-bold text-green-600 flex items-center gap-2">
                              <FileText size={20} />
                              {t("Classroom Environment") || "Classroom Environment"}
                            </h4>
                            {isSectionExpanded(observation.id, "classroom") ? (
                              <ChevronUp size={20} className="text-green-600" />
                            ) : (
                              <ChevronDown size={20} className="text-green-600" />
                            )}
                          </button>
                          {isSectionExpanded(observation.id, "classroom") && (
                            <div className="space-y-3 text-sm">
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Classroom Organization")}:</span>
                                <p className="text-gray-800 mt-1">{observation.classroomEnvironment.classroomOrganization || "N/A"}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Student Engagement")}:</span>
                                <p className="text-gray-800 mt-1">{observation.classroomEnvironment.studentEngagement || "N/A"}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Use of Learning Materials")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.classroomEnvironment.useOfLearningMaterials || "N/A"}
                                  {observation.classroomEnvironment.useOfLearningMaterialsDetails && (
                                    <span className="text-gray-600"> - {observation.classroomEnvironment.useOfLearningMaterialsDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Inclusivity")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.classroomEnvironment.inclusivity || "N/A"}
                                  {observation.classroomEnvironment.inclusivityDetails && (
                                    <span className="text-gray-600"> - {observation.classroomEnvironment.inclusivityDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Teacher Student Interaction")}:</span>
                                <p className="text-gray-800 mt-1">{observation.classroomEnvironment.teacherStudentInteraction || "N/A"}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Instructional Delivery */}
                      {observation.instructionalDelivery && (
                        <div className="border-l-4 border-purple-500 pl-4">
                          <button
                            onClick={() => toggleSection(observation.id, "instructional")}
                            className="w-full flex items-center justify-between text-left mb-3"
                          >
                            <h4 className="text-lg font-bold text-purple-600 flex items-center gap-2">
                              <FileText size={20} />
                              {t("Instructional Delivery") || "Instructional Delivery"}
                            </h4>
                            {isSectionExpanded(observation.id, "instructional") ? (
                              <ChevronUp size={20} className="text-purple-600" />
                            ) : (
                              <ChevronDown size={20} className="text-purple-600" />
                            )}
                          </button>
                          {isSectionExpanded(observation.id, "instructional") && (
                            <div className="space-y-3 text-sm">
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Lesson Planning")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.instructionalDelivery.lessonPlanning || "N/A"}
                                  {observation.instructionalDelivery.lessonPlanningDetails && (
                                    <span className="text-gray-600"> - {observation.instructionalDelivery.lessonPlanningDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Teaching Strategies")}:</span>
                                <p className="text-gray-800 mt-1">{observation.instructionalDelivery.teachingStrategies || "N/A"}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Questioning Techniques")}:</span>
                                <p className="text-gray-800 mt-1">{observation.instructionalDelivery.questioningTechniques || "N/A"}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Pace Time Management")}:</span>
                                <p className="text-gray-800 mt-1">{observation.instructionalDelivery.paceTimeManagement || "N/A"}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Content Knowledge")}:</span>
                                <p className="text-gray-800 mt-1">{observation.instructionalDelivery.contentKnowledge || "N/A"}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Technology Integration")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.instructionalDelivery.technologyIntegration || "N/A"}
                                  {observation.instructionalDelivery.technologyIntegrationDetails && (
                                    <span className="text-gray-600"> - {observation.instructionalDelivery.technologyIntegrationDetails}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Student Learning & Assessment */}
                      {observation.studentLearningAssessment && (
                        <div className="border-l-4 border-orange-500 pl-4">
                          <button
                            onClick={() => toggleSection(observation.id, "assessment")}
                            className="w-full flex items-center justify-between text-left mb-3"
                          >
                            <h4 className="text-lg font-bold text-orange-600 flex items-center gap-2">
                              <FileText size={20} />
                              {t("Student Learning Assessment") || "Student Learning & Assessment"}
                            </h4>
                            {isSectionExpanded(observation.id, "assessment") ? (
                              <ChevronUp size={20} className="text-orange-600" />
                            ) : (
                              <ChevronDown size={20} className="text-orange-600" />
                            )}
                          </button>
                          {isSectionExpanded(observation.id, "assessment") && (
                            <div className="space-y-3 text-sm">
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Assessment Methods Used")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.studentLearningAssessment.assessmentMethods || "N/A"}
                                  {observation.studentLearningAssessment.assessmentMethodsDetails && (
                                    <span className="text-gray-600"> - {observation.studentLearningAssessment.assessmentMethodsDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Student Participation")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.studentLearningAssessment.studentParticipation || "N/A"}
                                  {observation.studentLearningAssessment.studentParticipationDetails && (
                                    <span className="text-gray-600"> - {observation.studentLearningAssessment.studentParticipationDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Feedback Provided To Students")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.studentLearningAssessment.feedbackProvided || "N/A"}
                                  {observation.studentLearningAssessment.feedbackProvidedDetails && (
                                    <span className="text-gray-600"> - {observation.studentLearningAssessment.feedbackProvidedDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Evidence Of Learning Outcomes")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.studentLearningAssessment.evidenceOfLearning || "N/A"}
                                  {observation.studentLearningAssessment.evidenceOfLearningDetails && (
                                    <span className="text-gray-600"> - {observation.studentLearningAssessment.evidenceOfLearningDetails}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Professionalism & Reflection */}
                      {observation.professionalismReflection && (
                        <div className="border-l-4 border-indigo-500 pl-4">
                          <button
                            onClick={() => toggleSection(observation.id, "professionalism")}
                            className="w-full flex items-center justify-between text-left mb-3"
                          >
                            <h4 className="text-lg font-bold text-indigo-600 flex items-center gap-2">
                              <FileText size={20} />
                              {t("Professionalism Reflection") || "Professionalism & Reflection"}
                            </h4>
                            {isSectionExpanded(observation.id, "professionalism") ? (
                              <ChevronUp size={20} className="text-indigo-600" />
                            ) : (
                              <ChevronDown size={20} className="text-indigo-600" />
                            )}
                          </button>
                          {isSectionExpanded(observation.id, "professionalism") && (
                            <div className="space-y-3 text-sm">
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Preparedness Punctuality")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.professionalismReflection.preparedness || "N/A"}
                                  {observation.professionalismReflection.preparednessDetails && (
                                    <span className="text-gray-600"> - {observation.professionalismReflection.preparednessDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Professional Demeanor")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.professionalismReflection.professionalDemeanor || "N/A"}
                                  {observation.professionalismReflection.professionalDemeanorDetails && (
                                    <span className="text-gray-600"> - {observation.professionalismReflection.professionalDemeanorDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Collaboration With Colleagues")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.professionalismReflection.collaboration || "N/A"}
                                  {observation.professionalismReflection.collaborationDetails && (
                                    <span className="text-gray-600"> - {observation.professionalismReflection.collaborationDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Responsiveness To Feedback")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.professionalismReflection.responsivenessToFeedback || "N/A"}
                                  {observation.professionalismReflection.responsivenessToFeedbackDetails && (
                                    <span className="text-gray-600"> - {observation.professionalismReflection.responsivenessToFeedbackDetails}</span>
                                  )}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Reflective Practice")}:</span>
                                <p className="text-gray-800 mt-1">
                                  {observation.professionalismReflection.reflectivePractice || "N/A"}
                                  {observation.professionalismReflection.reflectivePracticeDetails && (
                                    <span className="text-gray-600"> - {observation.professionalismReflection.reflectivePracticeDetails}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Principal's Remarks */}
                      {observation.principalsRemarks && (
                        <div className="border-l-4 border-red-500 pl-4">
                          <h4 className="text-lg font-bold text-red-600 mb-3 flex items-center gap-2">
                            <FileText size={20} />
                            {t("Principals Remarks") || "Principal's Remarks"}
                          </h4>
                          <div className="space-y-3 text-sm">
                            <div className="bg-gray-50 p-3 rounded">
                              <span className="font-semibold text-gray-700">{t("Strengths Observed")}:</span>
                              <p className="text-gray-800 mt-1">{observation.principalsRemarks.strengthsObserved || "N/A"}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded">
                              <span className="font-semibold text-gray-700">{t("Areas For Improvement")}:</span>
                              <p className="text-gray-800 mt-1">{observation.principalsRemarks.areasForImprovement || "N/A"}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded">
                              <span className="font-semibold text-gray-700">{t("Action Plan Recommendations")}:</span>
                              <p className="text-gray-800 mt-1">{observation.principalsRemarks.actionPlan || "N/A"}</p>
                            </div>
                            {observation.principalsRemarks.followUpDate && (
                              <div className="bg-gray-50 p-3 rounded">
                                <span className="font-semibold text-gray-700">{t("Follow Up Date")}:</span>
                                <span className="ml-2 text-gray-800">{formatDate(observation.principalsRemarks.followUpDate)}</span>
                              </div>
                            )}
                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                              <span className="font-semibold text-blue-700">{t("Overall Rating Score")}:</span>
                              <span className="ml-2 text-blue-800 font-bold text-lg">
                                {observation.principalsRemarks.overallRating || "N/A"}
                                {observation.principalsRemarks.overallRatingDetails && (
                                  <span className="text-sm font-normal text-gray-600"> - {observation.principalsRemarks.overallRatingDetails}</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Attachments */}
                      {observation.attachments && observation.attachments.length > 0 && (
                        <div className="border-l-4 border-teal-500 pl-4">
                          <h4 className="text-lg font-bold text-teal-600 mb-3 flex items-center gap-2">
                            <Download size={20} />
                            {t("Attachments") || "Attachments"}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {observation.attachments.map((attachment, idx) => (
                              <a
                                key={idx}
                                href={attachment}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 p-3 rounded-lg border border-gray-200 transition-colors group"
                              >
                                <FileText size={20} className="text-teal-500 group-hover:text-teal-600" />
                                <span className="text-gray-700 group-hover:text-teal-600 font-medium">
                                  {t("Attachment")} {idx + 1}
                                </span>
                                <Download size={16} className="text-gray-400 group-hover:text-teal-600 ml-auto" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-xl font-medium mb-2">
                {t("No Observations Found") || "No observations found for this teacher"}
              </p>
              <p className="text-gray-400 text-sm mb-6">
                {t("Add First Observation Prompt") || "You can add a new observation by clicking the '+' button."}
              </p>
              {onAddNew && (
                <button
                  onClick={onAddNew}
                  className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-md"
                >
                  {t("Add First Observation") || "Add First Observation"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewAdminObservationsModal;
